/**
 * Bloomin 카드 입력 필드 — 공유 presentational 컴포넌트 (#701 SSOT).
 *
 * 카드번호/만료월/만료년/CVV/소유자명 5개 입력 필드 + 입력 마스킹(formatCardNumber/digitsOnly)
 * + 필드 단위 에러 표시를 담당하는 controlled 컴포넌트. AddCardModal(회원 카드 저장)과
 * BloominCardInputForm(게스트/즉석 결제·후불 charge·세션 합산) 양 wrapper 가 공유한다.
 *
 * 책임 범위에 **포함하지 않는 것**(각 wrapper 담당):
 *  - `<form>` 태그 / 제출 버튼 / 닉네임 입력 / 보안 알림(Lock)
 *  - 제출 시점 검증(buildBloominCardRegistrationPayload / validate) 및 PCI 메모리 폐기
 *  - anio 타입 변환(저장=number/confirm API, 결제=string/charge API)
 *
 * value 타입은 검증 SSOT 인 bloominRegistration 의 `BloominCardFormState` 를 그대로 재사용한다.
 */
import { useId } from 'react';
import { CreditCard } from 'lucide-react';
import {
  digitsOnly,
  formatCardNumber,
  type BloominCardFieldErrors,
  type BloominCardFormState,
} from './bloominRegistration';
import { CARD_BRAND_LABELS, detectCardBrand } from '@/utils/cardValidation';

export interface BloominCardFieldsLabels {
  cardNumberLabel: string;
  cardNumberPlaceholder: string;
  expiryMonthLabel: string;
  expiryYearLabel: string;
  cvvLabel: string;
  cvvPlaceholder: string;
  holderNameLabel: string;
  holderNamePlaceholder: string;
}

export interface BloominCardFieldsProps {
  value: BloominCardFormState;
  onChange: (next: BloominCardFormState) => void;
  labels: BloominCardFieldsLabels;
  /** 제출 시점 검증 결과(부모 책임). */
  fieldErrors?: BloominCardFieldErrors;
  /** 레거시 단일 에러 소비처 보존용. fieldErrors 가 있으면 fieldErrors 를 우선한다. */
  errorSummary?: string | null;
  /** 수정 중인 필드의 에러만 해제하려는 부모 wrapper 용 hook. */
  onFieldChange?: (field: keyof BloominCardFormState) => void;
  /** 테스트/aria 회귀를 위한 안정 id prefix. 미지정 시 useId 기반으로 중복을 피한다. */
  idPrefix?: string;
  brandTestId?: string;
  disabled?: boolean;
}

const FIELD_CLASS =
  'w-full px-[12px] py-[12px] border border-border rounded-[8px] text-[16px] disabled:opacity-50 aria-[invalid]:border-destructive';

export function BloominCardFields({
  value,
  onChange,
  labels,
  fieldErrors = {},
  errorSummary = null,
  onFieldChange,
  idPrefix,
  brandTestId = 'bloomin-card-brand',
  disabled = false,
}: BloominCardFieldsProps) {
  const generatedId = useId();
  const prefix = idPrefix ?? generatedId;
  const cardBrand = detectCardBrand(value.cardNumber);

  const ids = {
    cardNumber: `${prefix}-number`,
    cardNumberError: `${prefix}-number-error`,
    month: `${prefix}-month`,
    year: `${prefix}-year`,
    expiryError: `${prefix}-expiry-error`,
    cvv: `${prefix}-cvv`,
    cvvError: `${prefix}-cvv-error`,
    holder: `${prefix}-holder`,
    holderError: `${prefix}-holder-error`,
  };

  const updateField = (field: keyof BloominCardFormState, nextValue: string) => {
    onFieldChange?.(field);
    onChange({ ...value, [field]: nextValue });
  };

  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  return (
    <div className="space-y-[16px]">
      <div className="space-y-[8px]">
        <label htmlFor={ids.cardNumber} className="text-[14px] font-semibold text-foreground">
          {labels.cardNumberLabel}
        </label>
        <div className="relative">
          <CreditCard className="absolute left-[12px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-muted-foreground" />
          <input
            id={ids.cardNumber}
            type="text"
            inputMode="numeric"
            autoComplete="cc-number"
            maxLength={23}
            placeholder={labels.cardNumberPlaceholder}
            value={value.cardNumber}
            onChange={(e) => updateField('cardNumber', formatCardNumber(e.target.value))}
            disabled={disabled}
            className="w-full pl-[40px] pr-[88px] py-[12px] border border-border rounded-[8px] text-[16px] disabled:opacity-50 aria-[invalid]:border-destructive"
            aria-invalid={fieldErrors.cardNumber ? true : undefined}
            aria-describedby={fieldErrors.cardNumber ? ids.cardNumberError : undefined}
          />
          {cardBrand !== 'unknown' && (
            <span
              data-testid={brandTestId}
              className="absolute right-[12px] top-1/2 -translate-y-1/2 text-[12px] font-bold text-muted-foreground"
            >
              {CARD_BRAND_LABELS[cardBrand]}
            </span>
          )}
        </div>
        {fieldErrors.cardNumber && (
          <p id={ids.cardNumberError} role="alert" className="text-[13px] text-destructive">
            {fieldErrors.cardNumber}
          </p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-[12px]">
        <div className="space-y-[8px]">
          <label htmlFor={ids.month} className="text-[14px] font-semibold text-foreground">
            {labels.expiryMonthLabel}
          </label>
          <input
            id={ids.month}
            type="text"
            inputMode="numeric"
            autoComplete="cc-exp-month"
            maxLength={2}
            placeholder="MM"
            value={value.expiryMonth}
            onChange={(e) => updateField('expiryMonth', digitsOnly(e.target.value).slice(0, 2))}
            disabled={disabled}
            className={FIELD_CLASS}
            aria-invalid={fieldErrors.expiry ? true : undefined}
            aria-describedby={fieldErrors.expiry ? ids.expiryError : undefined}
          />
        </div>
        <div className="space-y-[8px]">
          <label htmlFor={ids.year} className="text-[14px] font-semibold text-foreground">
            {labels.expiryYearLabel}
          </label>
          <input
            id={ids.year}
            type="text"
            inputMode="numeric"
            autoComplete="cc-exp-year"
            maxLength={4}
            placeholder="YYYY"
            value={value.expiryYear}
            onChange={(e) => updateField('expiryYear', digitsOnly(e.target.value).slice(0, 4))}
            disabled={disabled}
            className={FIELD_CLASS}
            aria-invalid={fieldErrors.expiry ? true : undefined}
            aria-describedby={fieldErrors.expiry ? ids.expiryError : undefined}
          />
        </div>
        <div className="space-y-[8px]">
          <label htmlFor={ids.cvv} className="text-[14px] font-semibold text-foreground">
            {labels.cvvLabel}
          </label>
          <input
            id={ids.cvv}
            type="password"
            inputMode="numeric"
            autoComplete="cc-csc"
            maxLength={4}
            placeholder={labels.cvvPlaceholder}
            value={value.cvv}
            onChange={(e) => updateField('cvv', digitsOnly(e.target.value).slice(0, 4))}
            disabled={disabled}
            className={FIELD_CLASS}
            aria-invalid={fieldErrors.cvv ? true : undefined}
            aria-describedby={fieldErrors.cvv ? ids.cvvError : undefined}
          />
        </div>
      </div>

      {fieldErrors.expiry && (
        <p id={ids.expiryError} role="alert" className="text-[13px] text-destructive">
          {fieldErrors.expiry}
        </p>
      )}
      {fieldErrors.cvv && (
        <p id={ids.cvvError} role="alert" className="text-[13px] text-destructive">
          {fieldErrors.cvv}
        </p>
      )}

      <div className="space-y-[8px]">
        <label htmlFor={ids.holder} className="text-[14px] font-semibold text-foreground">
          {labels.holderNameLabel}
        </label>
        <input
          id={ids.holder}
          type="text"
          autoComplete="cc-name"
          maxLength={50}
          placeholder={labels.holderNamePlaceholder}
          value={value.holder}
          onChange={(e) => updateField('holder', e.target.value)}
          disabled={disabled}
          className={FIELD_CLASS}
          aria-invalid={fieldErrors.holder ? true : undefined}
          aria-describedby={fieldErrors.holder ? ids.holderError : undefined}
        />
        {fieldErrors.holder && (
          <p id={ids.holderError} role="alert" className="text-[13px] text-destructive">
            {fieldErrors.holder}
          </p>
        )}
      </div>

      {!hasFieldErrors && errorSummary && (
        <p role="alert" className="text-[13px] text-destructive">
          {errorSummary}
        </p>
      )}
    </div>
  );
}
